import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import { apiClient } from "@/lib/api/client";
import type { User } from "@/features/accounts/types/account.types";

import { clearHijackMarker, isHijacked } from "./hijack";
import { clearAccessToken, getAccessToken, setAccessToken } from "./token-store";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";
const PARENT_CSRF_COOKIE = "csrf_parent_refresh";

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : null;
}

const AUTH_USER_KEY = ["auth", "user"];

interface LoginResponse {
  access: string;
  user: User;
}

export function useCurrentUser() {
  return useQuery({
    queryKey: AUTH_USER_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<User>("/api/users/me/");
      return data;
    },
    // Only run once we hold an in-memory access token. On reload that's set by
    // `bootstrapSession()` (silent refresh) before this re-evaluates.
    enabled: !!getAccessToken(),
    retry: false,
    staleTime: Infinity,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (credentials: { username: string; password: string }) => {
      const { data } = await apiClient.post<LoginResponse>("/api/users/token/", credentials);
      return data;
    },
    onSuccess: (data) => {
      setAccessToken(data.access);
      queryClient.setQueryData(AUTH_USER_KEY, data.user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // While hijacking, "log out" releases the impersonation and the
      // admin keeps their original session — same UX as the legacy flow.
      if (isHijacked()) {
        const csrf = readCookie(PARENT_CSRF_COOKIE);
        if (csrf) {
          const { data } = await axios.post<{ access: string }>(
            `${API_BASE_URL}/api/users/hijack/release/`,
            null,
            {
              withCredentials: true,
              headers: { "X-Parent-CSRF-Refresh": csrf },
            },
          );
          setAccessToken(data.access);
        }
        clearHijackMarker();
        return { wasHijacked: true };
      }
      await apiClient.post("/api/users/logout/");
      return { wasHijacked: false };
    },
    onSuccess: (result) => {
      if (result?.wasHijacked) {
        queryClient.invalidateQueries({ queryKey: AUTH_USER_KEY });
        window.location.href = "/";
      } else {
        clearAccessToken();
        queryClient.clear();
        window.location.href = "/login";
      }
    },
    onError: () => {
      clearAccessToken();
      queryClient.clear();
      window.location.href = "/login";
    },
  });
}
